import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useDateFilter } from "@/context/DateFilterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Archive, Stethoscope, Link2, MessageCircle } from "lucide-react";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { toast } from "sonner";

const empty = { name: "", nps: 70, feedback_highlights: "", linked_idea_id: null };

export default function Services() {
  const { start, end } = useDateFilter();
  const [items, setItems] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/services", { params: { start, end } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/ideas").then((r) => setIdeas(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [start, end]);

  const ideaName = (id) => ideas.find((i) => i.id === id)?.title;
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s, nps: s.nps ?? 70 }); setEditId(s.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editId) await api.put(`/services/${editId}`, form);
      else await api.post("/services", form);
      setOpen(false); load(); toast.success(editId ? "Updated" : "Service added");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const archive = async (id) => { await api.delete(`/services/${id}`); load(); toast.success("Archived"); };
  const del = async (id) => { await api.delete(`/services/${id}`, { params: { permanent: true } }); load(); toast.success("Service deleted"); };

  const npsColor = (n) => n >= 75 ? "text-emerald-600" : n >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Services & Feedback Hub</h1>
          <p className="text-muted-foreground">Bridge current offerings with innovation needs via client feedback.</p>
        </div>
        <Button onClick={openNew} data-testid="new-service-btn"><Plus size={16} className="mr-1" /> New Service</Button>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {items.length === 0 && <p className="text-muted-foreground">No services in this period.</p>}
        {items.map((s) => (
          <Card key={s.id} className="flex flex-col p-5 border-slate-200 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`service-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-head font-bold text-slate-900 inline-flex items-center gap-2"><Stethoscope size={16} className="text-primary" /> {s.name}</h3>
              <div className="flex gap-1">
                <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-primary" data-testid={`edit-service-${s.id}`}><Pencil size={15} /></button>
                <button onClick={() => archive(s.id)} className="text-slate-400 hover:text-amber-600" data-testid={`archive-service-${s.id}`}><Archive size={15} /></button>
                <ConfirmDelete onConfirm={() => del(s.id)} name={s.name} testid={`delete-service-${s.id}`} />
              </div>
            </div>
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Client Satisfaction / NPS</span>
                <span className={`font-head text-lg font-extrabold ${npsColor(s.nps ?? 0)}`} data-testid={`service-nps-${s.id}`}>{s.nps ?? "—"}</span>
              </div>
              <Progress value={s.nps ?? 0} className="h-2" />
            </div>
            {s.feedback_highlights && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                <p className="mb-1 font-semibold text-slate-800 inline-flex items-center gap-1"><MessageCircle size={12} /> Feedback</p>
                {s.feedback_highlights}
              </div>
            )}
            {s.linked_idea_id && ideaName(s.linked_idea_id) && (
              <p className="mt-3 inline-flex items-center gap-1 text-xs text-secondary"><Link2 size={12} /> R&D: {ideaName(s.linked_idea_id)}</p>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="service-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit Service" : "New Service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Active Service Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" /></div>
            <div><Label>Target Metric / NPS ({form.nps})</Label><Input type="number" min={0} max={100} value={form.nps} onChange={(e) => setForm({ ...form, nps: Number(e.target.value) })} data-testid="service-nps-input" /></div>
            <div><Label>Client Feedback Highlights</Label><Textarea rows={3} value={form.feedback_highlights} onChange={(e) => setForm({ ...form, feedback_highlights: e.target.value })} placeholder="Pain points, requests, side-effect logs..." data-testid="service-feedback-input" /></div>
            <div><Label>Linked R&D Opportunity</Label>
              <Select value={form.linked_idea_id || "none"} onValueChange={(v) => setForm({ ...form, linked_idea_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="service-idea-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{ideas.map((i) => <SelectItem key={i.id} value={i.id}>{i.title.slice(0, 34)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-service-btn">{editId ? "Save Changes" : "Add Service"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
